import { useState } from 'react';
import { toast } from 'sonner';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TrainerCard } from '@/features/TrainerLayout/primitives/TrainerCard';
import {
  useHackathonDetail,
  useHackathonLeaderboard,
  useHackathonComponents,
  useHackathonTeams,
  useAddHackathonComponent,
  useAssignHackathonTeams,
  useGradeHackathonSubmission,
  useUpdateHackathonStatus,
} from '@/lib/hooks/useHackathons';
import type { HackathonComponentType, HackathonStatus } from '@/lib/api/hackathons';

const labelClass = 'mb-1 block text-xs font-medium text-[#6b7280]';

// Mirror the status→variant mapping used in the hackathons list and the
// org-admin BatchHackathonsTab so the same lifecycle status renders identically
// across every hackathon surface. `live` is the active state → primary
// (`default`); the rest are quieter.
const STATUS_VARIANT: Record<HackathonStatus, 'default' | 'secondary' | 'outline'> = {
  draft: 'secondary',
  scheduled: 'outline',
  live: 'default',
  completed: 'secondary',
};

// The only forward step a trainer can take from each status — matches the
// backend's monotonic lifecycle (draft -> scheduled/live -> completed).
// `completed` has no next step, so it's absent from this map.
const NEXT_STATUS: Partial<Record<HackathonStatus, { status: HackathonStatus; label: string }>> = {
  draft: { status: 'live', label: 'Start Hackathon' },
  scheduled: { status: 'live', label: 'Start Hackathon' },
  live: { status: 'completed', label: 'Mark Completed' },
};

/**
 * Route-agnostic hackathon detail surface (leaderboard, components, teams,
 * grading). It takes `batchId`/`hackathonId` as props instead of reading them
 * from a route-bound `Route.useParams()`, so the same full-access management UI
 * can be mounted under both the trainer and platform-admin batch route trees.
 */
export function HackathonDetailView({
  batchId,
  hackathonId,
}: {
  batchId: string;
  hackathonId: string;
}) {
  const { data: hackathon, isLoading } = useHackathonDetail(batchId, hackathonId);
  const { data: leaderboard } = useHackathonLeaderboard(batchId, hackathonId);

  if (isLoading || !hackathon) {
    return <div className="p-6 text-sm text-[#6b7280]">Loading…</div>;
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[#111827]">{hackathon.title}</h2>
        <div className="flex items-center gap-2">
          <Badge
            variant={STATUS_VARIANT[hackathon.status] ?? STATUS_VARIANT.draft}
            className="flex-shrink-0 capitalize"
          >
            {hackathon.status}
          </Badge>
          <StatusControl batchId={batchId} hackathonId={hackathonId} status={hackathon.status} />
        </div>
      </div>

      <TrainerCard className="mb-4 p-4">
        <h3 className="mb-2 text-sm font-semibold text-[#111827]">Live Leaderboard</h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="h-auto px-0 py-1">Rank</TableHead>
              <TableHead className="h-auto px-0 py-1">Team</TableHead>
              <TableHead className="h-auto px-0 py-1 text-right">Points</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leaderboard?.map((entry) => (
              <TableRow key={entry.team_id}>
                <TableCell className="px-0 py-1">#{entry.rank}</TableCell>
                <TableCell className="px-0 py-1">{entry.team_name}</TableCell>
                <TableCell className="px-0 py-1 text-right">{entry.score}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!leaderboard?.length && (
          <p className="py-4 text-center text-sm text-[#6b7280]">No scores yet.</p>
        )}
      </TrainerCard>

      <ComponentsSection batchId={batchId} hackathonId={hackathonId} />
      <TeamsSection batchId={batchId} hackathonId={hackathonId} />
      <GradingSection batchId={batchId} hackathonId={hackathonId} />
    </div>
  );
}

// ─── Status: advance the hackathon's lifecycle ───────────────────────────────────

function StatusControl({
  batchId,
  hackathonId,
  status,
}: {
  batchId: string;
  hackathonId: string;
  status: HackathonStatus;
}) {
  const { mutateAsync, isPending } = useUpdateHackathonStatus(batchId, hackathonId);
  const next = NEXT_STATUS[status];

  if (!next) {
    return null;
  }

  async function handleAdvance() {
    try {
      await mutateAsync({ status: next!.status });
      toast.success(`Hackathon marked ${next!.status}.`);
    } catch {
      toast.error('Failed to update status.');
    }
  }

  return (
    <Button size="sm" variant="outline" onClick={handleAdvance} disabled={isPending}>
      {next.label}
    </Button>
  );
}

// ─── Components: list + add ──────────────────────────────────────────────────────

function ComponentsSection({ batchId, hackathonId }: { batchId: string; hackathonId: string }) {
  const { data: components } = useHackathonComponents(batchId, hackathonId);
  const { mutateAsync, isPending } = useAddHackathonComponent(batchId, hackathonId);

  const [type, setType] = useState<HackathonComponentType>('problem');
  const [maxPoints, setMaxPoints] = useState(100);
  const [problemId, setProblemId] = useState('');
  const [projectTitle, setProjectTitle] = useState('');
  const [projectBrief, setProjectBrief] = useState('');

  function reset() {
    setType('problem');
    setMaxPoints(100);
    setProblemId('');
    setProjectTitle('');
    setProjectBrief('');
  }

  async function handleAdd() {
    if (maxPoints <= 0) {
      toast.error('Max points must be greater than zero.');
      return;
    }
    if (type === 'problem' && !problemId.trim()) {
      toast.error('A problem ID is required for a problem component.');
      return;
    }
    if (type === 'project' && !projectTitle.trim()) {
      toast.error('A project title is required for a project component.');
      return;
    }
    try {
      await mutateAsync({
        type,
        max_points: maxPoints,
        problem_id: type === 'problem' ? problemId.trim() : undefined,
        project_title: type === 'project' ? projectTitle.trim() : undefined,
        project_brief: type === 'project' ? projectBrief.trim() || undefined : undefined,
      });
      toast.success('Component added.');
      reset();
    } catch {
      toast.error('Failed to add component.');
    }
  }

  return (
    <TrainerCard className="mb-4 p-4">
      <h3 className="mb-2 text-sm font-semibold text-[#111827]">Components</h3>
      {!components?.length ? (
        <p className="mb-3 text-sm text-[#6b7280]">No components yet.</p>
      ) : (
        <ul className="mb-3 space-y-1 text-sm">
          {components.map((c) => (
            <li key={c.id} className="flex items-center justify-between border-t border-[#ececec] py-1 first:border-t-0">
              <span>
                <span className="capitalize text-[#111827]">{c.type}</span>
                <span className="text-[#6b7280]"> · {c.problem_title ?? c.project_title ?? c.problem_id ?? 'Untitled'}</span>
              </span>
              <span className="text-xs text-[#6b7280]">{c.max_points} pts</span>
            </li>
          ))}
        </ul>
      )}

      <div className="grid gap-3 border-t border-[#ececec] pt-3">
        <div>
          <label className={labelClass} htmlFor="hc-type">Type</label>
          <Select value={type} onValueChange={(v) => setType(v as HackathonComponentType)}>
            <SelectTrigger id="hc-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="problem">Problem</SelectItem>
              <SelectItem value="project">Project</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className={labelClass} htmlFor="hc-max-points">Max points</label>
          <Input
            id="hc-max-points"
            type="number"
            min={1}
            value={maxPoints}
            onChange={(e) => setMaxPoints(Number(e.target.value))}
          />
        </div>
        {type === 'problem' ? (
          <div>
            <label className={labelClass} htmlFor="hc-problem-id">Problem ID</label>
            <Input
              id="hc-problem-id"
              value={problemId}
              onChange={(e) => setProblemId(e.target.value)}
              placeholder="prob_abc123"
            />
          </div>
        ) : (
          <>
            <div>
              <label className={labelClass} htmlFor="hc-project-title">Project title</label>
              <Input
                id="hc-project-title"
                value={projectTitle}
                onChange={(e) => setProjectTitle(e.target.value)}
                placeholder="Build a URL shortener"
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="hc-project-brief">Project brief</label>
              <Input
                id="hc-project-brief"
                value={projectBrief}
                onChange={(e) => setProjectBrief(e.target.value)}
                placeholder="Optional description"
              />
            </div>
          </>
        )}
        <div>
          <Button size="sm" onClick={handleAdd} disabled={isPending}>Add component</Button>
        </div>
      </div>
    </TrainerCard>
  );
}

// ─── Teams: list + assign ────────────────────────────────────────────────────────

function TeamsSection({ batchId, hackathonId }: { batchId: string; hackathonId: string }) {
  const { data: teams } = useHackathonTeams(batchId, hackathonId);
  const { mutateAsync, isPending } = useAssignHackathonTeams(batchId, hackathonId);

  const [drafts, setDrafts] = useState<{ name: string; member_ids: string[] }[]>([]);
  const [name, setName] = useState('');
  const [memberIds, setMemberIds] = useState('');

  function addDraft() {
    const trimmedName = name.trim();
    const ids = memberIds.split(',').map((s) => s.trim()).filter(Boolean);
    if (!trimmedName) {
      toast.error('Team name is required.');
      return;
    }
    if (!ids.length) {
      toast.error('At least one member ID is required.');
      return;
    }
    setDrafts((prev) => [...prev, { name: trimmedName, member_ids: ids }]);
    setName('');
    setMemberIds('');
  }

  async function handleAssign() {
    if (!drafts.length) {
      toast.error('Add at least one team before saving.');
      return;
    }
    try {
      await mutateAsync({ teams: drafts });
      toast.success('Teams assigned.');
      setDrafts([]);
    } catch {
      toast.error('Failed to assign teams.');
    }
  }

  return (
    <TrainerCard className="mb-4 p-4">
      <h3 className="mb-2 text-sm font-semibold text-[#111827]">Teams</h3>
      {!teams?.length ? (
        <p className="mb-3 text-sm text-[#6b7280]">No teams assigned yet.</p>
      ) : (
        <ul className="mb-3 space-y-1 text-sm">
          {teams.map((t) => (
            <li key={t.id} className="flex items-center justify-between border-t border-[#ececec] py-1 first:border-t-0">
              <span className="text-[#111827]">{t.name}</span>
              <span className="text-xs text-[#6b7280]">{t.member_ids.length} member{t.member_ids.length !== 1 ? 's' : ''}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="grid gap-3 border-t border-[#ececec] pt-3">
        {drafts.length > 0 && (
          <ul className="space-y-1 text-sm">
            {drafts.map((d, i) => (
              <li key={`${d.name}-${i}`} className="flex items-center justify-between rounded-lg bg-[#fafafa] px-2 py-1">
                <span className="text-[#111827]">{d.name}</span>
                <span className="text-xs text-[#6b7280]">{d.member_ids.length} member{d.member_ids.length !== 1 ? 's' : ''}</span>
              </li>
            ))}
          </ul>
        )}
        <div>
          <label className={labelClass} htmlFor="ht-name">Team name</label>
          <Input
            id="ht-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Team Rocket"
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="ht-members">Member IDs (comma-separated)</label>
          <Input
            id="ht-members"
            value={memberIds}
            onChange={(e) => setMemberIds(e.target.value)}
            placeholder="stu_1, stu_2"
          />
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={addDraft}>Add team</Button>
          <Button size="sm" onClick={handleAssign} disabled={isPending}>Save teams</Button>
        </div>
      </div>
    </TrainerCard>
  );
}

// ─── Grading: grade a project/problem submission ─────────────────────────────────

function GradingSection({ batchId, hackathonId }: { batchId: string; hackathonId: string }) {
  const { mutateAsync, isPending } = useGradeHackathonSubmission(batchId, hackathonId);

  const [submissionId, setSubmissionId] = useState('');
  const [score, setScore] = useState('');
  const [feedback, setFeedback] = useState('');

  async function handleGrade() {
    const scoreNum = Number(score);
    if (!submissionId.trim()) {
      toast.error('A submission ID is required.');
      return;
    }
    if (score === '' || isNaN(scoreNum) || scoreNum < 0) {
      toast.error('Enter a valid, non-negative score.');
      return;
    }
    try {
      await mutateAsync({
        submissionId: submissionId.trim(),
        req: { score: scoreNum, feedback },
      });
      toast.success('Submission graded.');
      setSubmissionId('');
      setScore('');
      setFeedback('');
    } catch {
      toast.error('Failed to grade submission.');
    }
  }

  return (
    <TrainerCard className="mb-4 p-4">
      <h3 className="mb-2 text-sm font-semibold text-[#111827]">Grade a submission</h3>
      <div className="grid gap-3">
        <div>
          <label className={labelClass} htmlFor="hg-submission-id">Submission ID</label>
          <Input
            id="hg-submission-id"
            value={submissionId}
            onChange={(e) => setSubmissionId(e.target.value)}
            placeholder="sub_abc123"
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="hg-score">Score</label>
          <Input
            id="hg-score"
            type="number"
            min={0}
            value={score}
            onChange={(e) => setScore(e.target.value)}
            placeholder="0"
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="hg-feedback">Feedback (optional)</label>
          <textarea
            id="hg-feedback"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={3}
            placeholder="Add feedback for the team…"
            className="w-full rounded-lg border border-[#ececec] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#ff5a1f]"
          />
        </div>
        <div>
          <Button size="sm" onClick={handleGrade} disabled={isPending}>Mark graded</Button>
        </div>
      </div>
    </TrainerCard>
  );
}
